export interface PwaLifecycleCallbacks {
  readonly onUpdateReady?: (activate: () => Promise<void>) => void;
  readonly onOfflineReady?: () => void;
  readonly onError?: (error: unknown) => void;
}

export interface PwaRegistration {
  readonly registration: ServiceWorkerRegistration;
  checkForUpdate(): Promise<void>;
}

export async function registerPwaWorker(
  callbacks: PwaLifecycleCallbacks = {},
): Promise<PwaRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
      type: "module",
      updateViaCache: "none",
    });
    const announceWaiting = () => {
      const waiting = registration.waiting;
      if (!waiting) return;
      callbacks.onUpdateReady?.(async () => {
        const reloading = new Promise<void>((resolve) => {
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => {
              resolve();
              location.reload();
            },
            { once: true },
          );
        });
        waiting.postMessage({ type: "SKIP_WAITING" });
        await reloading;
      });
    };
    announceWaiting();
    registration.addEventListener("updatefound", () => {
      const worker = registration.installing;
      worker?.addEventListener("statechange", () => {
        if (
          worker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          announceWaiting();
        } else if (worker.state === "installed") {
          callbacks.onOfflineReady?.();
        }
      });
    });
    return {
      registration,
      async checkForUpdate() {
        await registration.update();
      },
    };
  } catch (error) {
    callbacks.onError?.(error);
    return null;
  }
}
